//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import {
  IContentItemCampaign,
  IContentLibraryItem,
} from '@apis/content-library/contentLibraryApiModel';
import { BV_SAMPLING_PROGRAM } from '@campaigns/constants/sampling/SamplingCampaignRequestConstants';
import {
  SamplingCampaignId,
  SamplingCampaignIdFactory,
} from '@models/SamplingCampaignId';

/**
 * Finds the BV sampling campaign a content item belongs to and returns its
 * numeric sampling campaign id (e.g. `bvsampling_..._104283` -> 104283), or
 * `undefined` if the item is not part of a sampling campaign.
 */
export const getSamplingCampaignId = (
  item: IContentLibraryItem,
): SamplingCampaignId | undefined => {
  const samplingCampaign = item.campaigns?.find((campaign) =>
    campaign.campaignId.startsWith(BV_SAMPLING_PROGRAM),
  );

  const numericId = samplingCampaign?.campaignId.split('_').pop();

  if (!numericId || !/^\d+$/.test(numericId)) {
    return undefined;
  }

  return SamplingCampaignIdFactory(parseInt(numericId, 10));
};

/**
 * Whether any of the item's campaigns is a BV sampling campaign (its
 * `campaignId` starts with `bvsampling`).
 */
export const hasBvSamplingCampaign = (
  campaigns: IContentItemCampaign[] = [],
): boolean =>
  campaigns.some((campaign) =>
    campaign.campaignId.startsWith(BV_SAMPLING_PROGRAM),
  );
